import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Facebook, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PageShell } from "../components/layout/PageShell";
import { PageHero } from "../components/layout/PageHero";

const SERVICES = ["Dog Walking", "In-home Pet Sitting", "Home Visits", "Something else"];
const DAYS = ["Monday", "Tuesday", "Friday", "Other / one-off"];

const inputCls =
  "w-full rounded-xl border border-[#DDD7CB] bg-white px-4 py-3 text-sm text-[#292A26] placeholder:text-[#575953]/50 focus:outline-none focus:border-[#3F4A38] focus:ring-2 focus:ring-[#3F4A38]/15 transition";
const labelCls = "block text-xs font-semibold uppercase tracking-[0.14em] text-[#575953] mb-2";

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const requestedService = searchParams.get("service");
  const [form, setForm] = useState({
    name: "", email: "", phone: "", location: "",
    service: SERVICES.includes(requestedService) ? requestedService : "Dog Walking",
    preferred_days: [], pet_details: "", dates: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleDay = (d) =>
    setForm({ ...form, preferred_days: form.preferred_days.includes(d)
      ? form.preferred_days.filter((x) => x !== d)
      : [...form.preferred_days, d] });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      toast.error("Something went wrong. Please try again, or email marypawpinsinverness@gmail.com directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PageShell title="Contact">
      <PageHero
        testId="contact-hero"
        eyebrow="Contact"
        title="Let's talk about your pet."
        copy="Whether you are looking for a regular walking place or planning time away, tell Carly a little about your pet and what you need."
        image="/images/marypawpins/carly-kneel-cocker-pinewoods.webp"
        alt="Carly kneeling with a cocker spaniel in the pinewoods"
        imgPosition="object-[50%_30%]"
      />

      <section className="py-14 sm:py-20" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            {sent ? (
              <div className="bg-white rounded-3xl border border-[#DDD7CB] p-10 text-center" data-testid="enquiry-success">
                <CheckCircle2 className="w-10 h-10 text-[#3F4A38] mx-auto" />
                <h2 className="font-serif text-2xl sm:text-3xl font-medium text-[#292A26] mt-5">Thank you. Your enquiry is on its way.</h2>
                <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#575953] max-w-md mx-auto">
                  Carly reviews every enquiry personally and will be in touch to discuss
                  availability, suitability and next steps. This doesn't confirm a booking
                  yet. It's the start of the conversation.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl border border-[#DDD7CB] p-7 sm:p-10" data-testid="enquiry-form">
                <h2 className="font-serif text-2xl font-medium text-[#292A26]">Enquire about availability</h2>
                <p className="mt-2 text-sm text-[#575953]">
                  The more you can share about your pet, the better Carly can help.
                </p>

                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls} htmlFor="enq-name">Your name *</label>
                    <input id="enq-name" data-testid="enquiry-name" required className={inputCls} value={form.name} onChange={set("name")} placeholder="Full name" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="enq-email">Email *</label>
                    <input id="enq-email" data-testid="enquiry-email" required type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="enq-phone">Phone</label>
                    <input id="enq-phone" data-testid="enquiry-phone" type="tel" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="07…" />
                  </div>
                  <div>
                    <label className={labelCls} htmlFor="enq-location">Where are you based? *</label>
                    <input id="enq-location" data-testid="enquiry-location" required className={inputCls} value={form.location} onChange={set("location")} placeholder="Area of Inverness" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="enq-service">Service *</label>
                    <select id="enq-service" data-testid="enquiry-service" className={inputCls} value={form.service} onChange={set("service")}>
                      {SERVICES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <span className={labelCls}>Preferred days (for regular walking)</span>
                    <div className="flex flex-wrap gap-2.5">
                      {DAYS.map((d) => (
                        <button
                          type="button"
                          key={d}
                          data-testid={`enquiry-day-${d.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          onClick={() => toggleDay(d)}
                          className={`rounded-full px-4 py-2 text-sm font-medium border transition-colors ${
                            form.preferred_days.includes(d)
                              ? "bg-[#3F4A38] text-[#F7F4ED] border-[#3F4A38]"
                              : "bg-white text-[#575953] border-[#DDD7CB] hover:border-[#3F4A38]/50"
                          }`}
                        >
                          {d}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="enq-pets">Your pet(s) *</label>
                    <textarea id="enq-pets" data-testid="enquiry-pets" required rows={2} className={inputCls} value={form.pet_details} onChange={set("pet_details")} placeholder="Names, species, breed, ages, and how many" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="enq-dates">Dates (if planning time away)</label>
                    <input id="enq-dates" data-testid="enquiry-dates" className={inputCls} value={form.dates} onChange={set("dates")} placeholder="e.g. 12–19 August" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls} htmlFor="enq-message">Anything else Carly should know *</label>
                    <textarea id="enq-message" data-testid="enquiry-message" required rows={4} className={inputCls} value={form.message} onChange={set("message")} placeholder="Temperament, routines, health, recall, what you're looking for…" />
                  </div>
                </div>

                <p className="mt-5 text-xs text-[#575953] leading-relaxed">
                  Your details are used only to respond to your enquiry. Home addresses,
                  keys and care records are arranged securely later, once a booking is agreed.
                </p>

                <button
                  type="submit"
                  disabled={sending}
                  data-testid="enquiry-submit"
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#3F4A38] text-[#F7F4ED] px-8 py-3.5 text-sm font-semibold hover:bg-[#2F382B] transition-colors disabled:opacity-60"
                >
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {sending ? "Sending…" : "Send Enquiry"}
                </button>
              </form>
            )}
          </div>

          <aside className="lg:col-span-5 space-y-6">
            <div className="bg-[#3F4A38] text-[#F7F4ED] rounded-3xl p-8" data-testid="contact-details-card">
              <h2 className="font-serif text-2xl font-medium">Prefer to reach out directly?</h2>
              <ul className="mt-6 space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-[#D8B7B0]" />
                  <a href="mailto:marypawpinsinverness@gmail.com" data-testid="contact-email" className="hover:text-white transition-colors">marypawpinsinverness@gmail.com</a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-[#D8B7B0]" />
                  <a href="tel:+447749040175" data-testid="contact-phone" className="hover:text-white transition-colors">07749 040175</a>
                </li>
                <li className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#D8B7B0]" />
                  <span>Inverness &amp; the Highlands</span>
                </li>
              </ul>
              <div className="mt-6 flex items-center gap-3">
                <a href="https://www.instagram.com/marypawpins.inverness/" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="contact-instagram" className="w-10 h-10 rounded-full border border-[#F7F4ED]/25 flex items-center justify-center hover:bg-[#F7F4ED]/10 transition-colors">
                  <Instagram className="w-4 h-4" />
                </a>
                <a href="https://www.facebook.com/profile.php?id=61586003890175" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="contact-facebook" className="w-10 h-10 rounded-full border border-[#F7F4ED]/25 flex items-center justify-center hover:bg-[#F7F4ED]/10 transition-colors">
                  <Facebook className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="bg-[#E7E2D8]/60 border border-[#DDD7CB] rounded-3xl p-8" data-testid="contact-area-card">
              <h3 className="font-serif text-xl font-medium text-[#292A26]">Service area</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#575953]">
                Regular dog walking covers Inverness, subject to route availability.
                Pet-sitting travel is considered individually, so mention your location
                when you enquire.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#575953]">
                Regular walking days: <span className="font-semibold text-[#292A26]">Monday, Tuesday and Friday.</span>
              </p>
            </div>
          </aside>
        </div>
      </section>
    </PageShell>
  );
}
